/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { memo, useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAnalytics } from '../common/controller/analytics/use-analytics';
import { ErrorBoundary } from '../common/ui/error-boundary';
import { KnowledgeBadge } from '../knowledge-card-components/knowledge-badge';
import { RelatedEntityType } from '../related-entities-api/types';

import { onRelatedEntityClick, onSurfaceShown } from './analytics';
import { AITopicsFooter } from './common/ai-topics-footer';
import { AITopicsSentimentFeedback } from './common/ai-topics-sentiment-feedback';
import { messages } from './messages';
import { useRelatedTopics } from './use-related-topics';

const styles = cssMap({
	root: {
		backgroundColor: token('elevation.surface.raised'),
		borderRadius: token('radius.small'),
		boxShadow: token('elevation.shadow.raised'),
		marginTop: token('space.200'),
	},
});

const TOPICS_CARD_FEEDBACK_CONTEXT = 'AI topics from topics card in advanced search';

type Props = {
	documentId?: string;
	documentType?: string;
	searchQuery: string;
	cloudId: string;
	userId: string;
	userName: string;
};

const documentMap: Record<string, RelatedEntityType> = {
	page: RelatedEntityType.CONFLUENCE_PAGE,
	blogpost: RelatedEntityType.CONFLUENCE_BLOGPOST,
};

const AITopicsCardByDocumentComponent = memo(
	({ documentId, documentType, searchQuery, cloudId, userId, userName }: Props) => {
		const { formatMessage } = useIntl();
		const [fireAnalyticsEvent] = useAnalytics();
		const { aiRelatedTopics, fetchRelatedTopics, showTopics, resetTopics } = useRelatedTopics();

		const principalEntityType = documentMap[documentType ?? ''];
		const principalEntityId = documentId ?? '';

		const feedbackAttributes = {
			feedbackContext: TOPICS_CARD_FEEDBACK_CONTEXT,
			principalEntityId,
			principalEntityType,
		};
		const userInfo = { id: userId, name: userName };

		const onBadgeClickHandler = (topicId: string) => {
			fireAnalyticsEvent(
				onRelatedEntityClick({
					clickedEntityId: topicId,
					clickedEntityType: RelatedEntityType.TOPIC,
					principalEntityType,
					principalEntityId,
					location: 'topicCardAdvancedSearch',
				}),
			);
		};

		useEffect(() => {
			if (!principalEntityType || !searchQuery) {
				resetTopics();
			} else if (principalEntityType) {
				fetchRelatedTopics({ entityId: principalEntityId, principalEntityType, cloudId });
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [principalEntityType, searchQuery, resetTopics, principalEntityId, cloudId]);

		useEffect(() => {
			if (showTopics) {
				fireAnalyticsEvent(
					onSurfaceShown({
						principalEntityId,
						principalEntityType,
						relatedTopics: aiRelatedTopics.map((topic) => topic.id),
						location: 'topicCardAdvancedSearch',
					}),
				);
			}
		}, [showTopics, fireAnalyticsEvent, principalEntityId, principalEntityType, aiRelatedTopics]);

		if (!showTopics) {
			return null;
		}

		return (
			<Box xcss={styles.root} testId="ai-topics-card-by-document">
				<Box padding="space.200" paddingBlockEnd="space.100">
					<Stack space="space.100">
						<FormattedMessage
							{...messages.ai_topics_card_header_text}
							values={{ searchQuery: <Text weight="bold">{searchQuery}</Text> }}
						/>
						<Inline space="space.100" rowSpace="space.100" shouldWrap>
							{aiRelatedTopics.map((topic) => (
								<KnowledgeBadge
									key={topic.id}
									name={topic.name}
									url={`/wiki/topics/${topic.id}`}
									onBadgeClick={() => onBadgeClickHandler(topic.id)}
								/>
							))}
						</Inline>
						<AITopicsSentimentFeedback
							attributes={feedbackAttributes}
							prompt={formatMessage(messages.ai_topics_card_feedback_prompt)}
							location="aiTopicsCardAdvancedSearch"
							userDetails={userInfo}
						/>
					</Stack>
				</Box>
				<AITopicsFooter />
			</Box>
		);
	},
);

export const AITopicsCardByDocument = (props: Props) => (
	<ErrorBoundary>
		<AITopicsCardByDocumentComponent {...props} />
	</ErrorBoundary>
);
